import { useCallback, useMemo, useRef, useState } from 'react';

import { FlowConsumerContextEnergy } from '../../../../context';
import {
  checkOfferIncludesNonDynamicPricingProduct,
  checkOffersIncludesDynamicPricingProduct,
  getOffers as getOffersHelper,
} from '../../../../shared/helpers';
import { filterOffersByType, sortOffersBasedOnTimeOfUse } from '../../../../shared/helpers/misc';
import { useGetOffers } from '../../../../shared/hooks';
import { type Offer, type OfferResponse } from '../../../../shared/types';
import { touExperimentFeatureToggleKey, touExperimentProductFilter } from '../../../growthbook/experiment.helpers';

interface ExperimentConfig {
  experimentKey: string;
  filterPredicate: (offer: Offer, variant: string | null) => boolean;
}

interface UseOfferFilteringParams {
  getOffersConfig?: ExperimentConfig | null;
  transformOffers?: (filteredOffers: Offer[], allFilteredOffers: Offer[]) => Offer[];
  showProductToggleOverride?: (defaultValue: boolean) => boolean;
  onProductFilterChange?: () => void;
  flowContext: FlowConsumerContextEnergy;
  offer?: OfferResponse;
}

export function useOfferFiltering({
  getOffersConfig = {
    experimentKey: touExperimentFeatureToggleKey,
    filterPredicate: touExperimentProductFilter,
  },
  transformOffers,
  showProductToggleOverride,
  onProductFilterChange,
  flowContext,
  offer,
}: UseOfferFilteringParams) {
  const [productFilter, setProductFilter] = useState('');

  const useExperimentFiltering = getOffersConfig !== null;

  let experimentConfig;
  if (useExperimentFiltering && getOffersConfig?.experimentKey) {
    experimentConfig = {
      experimentKey: getOffersConfig.experimentKey,
      filterPredicate: getOffersConfig.filterPredicate,
    };
  }

  const { filterOffers } = useGetOffers(experimentConfig);

  const allOffers = useMemo(() => {
    if (useExperimentFiltering) {
      return filterOffers({
        context: { ...flowContext, hasCrossSellRentalDevice: false },
        offer,
        compareFn: sortOffersBasedOnTimeOfUse,
      });
    }
    return getOffersHelper({ ...flowContext, hasCrossSellRentalDevice: false }, offer);
  }, [useExperimentFiltering, filterOffers, flowContext, offer]);

  // Hold transformOffers in a ref so `offers` only recomputes when `allOffers` changes.
  const transformOffersRef = useRef(transformOffers);
  transformOffersRef.current = transformOffers;

  const offers = useMemo(
    () => (transformOffersRef.current ? transformOffersRef.current(allOffers, allOffers) : allOffers),
    [allOffers],
  );

  const defaultShowProductToggle =
    checkOffersIncludesDynamicPricingProduct(offers) &&
    !!offers.some(o => checkOfferIncludesNonDynamicPricingProduct(o));

  const showProductToggle = showProductToggleOverride
    ? showProductToggleOverride(defaultShowProductToggle)
    : defaultShowProductToggle;

  const availableOffers = useMemo(() => {
    if (!showProductToggle) {
      return offers;
    }
    if (!productFilter) {
      return [];
    }

    return filterOffersByType(offers, productFilter);
  }, [offers, productFilter, showProductToggle]);

  const handleValueChange = useCallback(
    (value: string) => {
      setProductFilter(value);
      onProductFilterChange?.();
    },
    [onProductFilterChange],
  );

  return {
    offers,
    allOffers,
    availableOffers,
    showProductToggle,
    productFilter,
    setProductFilter,
    handleValueChange,
  };
}
